import { useEffect, useRef, useState } from "react";
import { api, formatApiError, getToken, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, Key, Fingerprint, ShieldCheck, ShieldWarning, ArrowSquareOut, FileText } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

async function callBridge(url, payload) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 4000);
  try {
    const r = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload), signal: ctrl.signal });
    if (!r.ok) throw new Error("bridge " + r.status);
    return await r.json();
  } finally { clearTimeout(t); }
}

export default function SigningPanel({ doc, onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [verify, setVerify] = useState(null);
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState("");
  const [bridgeMissing, setBridgeMissing] = useState(null);
  const [party, setParty] = useState("disclosing");
  const [signer, setSigner] = useState({ name: "", identifier: "" });
  const fileRef = useRef();
  const hasPdf = !!doc.current_pdf_id;
  const locked = ["partially_signed", "fully_signed"].includes(doc.status);

  const refresh = async () => {
    const [{ data: c }, { data: r }] = await Promise.all([api.get("/legal/signing/config"), api.get(`/legal/${doc._id}/esign/requests`)]);
    setCfg(c); setRequests(r);
    if (doc.current_pdf_id) { try { const { data: v } = await api.get(`/legal/${doc._id}/verify`); setVerify(v); } catch { setVerify(null); } }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [doc._id, doc.current_pdf_id, doc.status]);

  useEffect(() => {
    const pending = requests.some((r) => r.status === "requested");
    if (!pending) return;
    const id = setInterval(async () => {
      const { data: r } = await api.get(`/legal/${doc._id}/esign/requests`);
      setRequests(r);
      if (r.some((x) => x.status === "completed" && !requests.find((y) => y.txn === x.txn && y.status === "completed"))) onChanged();
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [requests, doc._id]);

  const upload = async (f) => {
    if (!f) return;
    setBusy("upload");
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await fetch(`${BASE}/api/legal/${doc._id}/pdf`, { method: "POST", headers: authHeaders(), body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Upload failed");
      toast.success(`PDF uploaded · SHA-256 ${data.sha256.slice(0, 12)}…`); onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  const generatePdf = async () => {
    setBusy("gen");
    try { const { data } = await api.post(`/legal/${doc._id}/pdf/generate`); toast.success(`PDF generated · SHA-256 ${data.sha256.slice(0, 12)}…`); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const openPdf = async () => {
    const r = await fetch(`${BASE}/api/legal/${doc._id}/pdf`, { headers: authHeaders() });
    const blob = await r.blob(); window.open(URL.createObjectURL(blob), "_blank");
  };

  const partyName = party === "disclosing" ? (doc.disclosing_party?.name || "Asymmetry Edits") : (doc.receiving_party?.name || "Counterparty");

  const signDsc = async () => {
    setBusy("dsc"); setBridgeMissing(null);
    try {
      let bridge = null;
      try { bridge = await callBridge(cfg.bridge_url, { hash_algorithm: "SHA-256", hash_is_digest: true, request_certificate: true }); }
      catch { bridge = null; }
      if (!bridge?.certificate_der_b64) {
        setBridgeMissing(true);
        if (cfg.dsc_mode !== "sandbox") { toast.error("DSC signer bridge not detected"); return; }
        return;
      }
      const { data: prep } = await api.post(`/legal/${doc._id}/dsc/prepare`, { signer_name: partyName, party, designation: "Authorised Signatory", certificate_der_b64: bridge.certificate_der_b64 });
      const sig = await callBridge(cfg.bridge_url, { hash_b64: prep.signed_attributes_sha256, hash_algorithm: "SHA-256", hash_is_digest: true, certificate_der_b64: bridge.certificate_der_b64 });
      await api.post(`/legal/${doc._id}/dsc/finalize`, { session_id: prep.session_id, signature_b64: sig.signature_b64, certificate_der_b64: bridge.certificate_der_b64 });
      toast.success("DSC signature embedded & verified"); onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const sandboxDsc = async () => {
    setBusy("dsc");
    try {
      const { data: prep } = await api.post(`/legal/${doc._id}/dsc/prepare`, { signer_name: partyName, party, designation: "Authorised Signatory" });
      await api.post(`/legal/${doc._id}/dsc/sandbox-sign`, { session_id: prep.session_id });
      toast.success("Sandbox DSC signature embedded (test certificate)"); setBridgeMissing(null); onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const requestEsign = async (e) => {
    e.preventDefault(); setBusy("esign");
    try {
      const { data } = await api.post(`/legal/${doc._id}/esign/request`, { signer_name: signer.name || partyName, identifier: signer.identifier, party, designation: "Authorised Signatory" });
      toast.success(`eSign request created (${data.provider})`);
      if (data.sign_url) window.open(data.sign_url, "_blank", "noopener");
      setSigner({ name: "", identifier: "" }); refresh(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="mt-8 space-y-6" data-testid="signing-panel">
      <div className="label-eyebrow">Finalized PDF & signing</div>

      <div className="card-soft p-5 flex flex-wrap items-center gap-3" data-testid="pdf-section">
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="nda-pdf-input" />
        {!locked && <button className="btn-primary btn-sm" disabled={busy === "upload"} onClick={() => fileRef.current?.click()} data-testid="nda-upload-btn"><UploadSimple size={13} /> {hasPdf ? "Replace PDF" : "Upload finalized NDA PDF"}</button>}
        {!locked && <button className="btn-ghost btn-sm" disabled={busy === "gen"} onClick={generatePdf} data-testid="nda-generate-pdf"><FileText size={13} /> {hasPdf ? "Re-generate from text" : "Generate PDF from text"}</button>}
        {hasPdf && <button className="btn-ghost btn-sm" onClick={openPdf} data-testid="nda-open-pdf"><DownloadSimple size={13} /> Open {doc.pdf_filename || "PDF"}</button>}
        {hasPdf ? <span className="text-xs text-muted-foreground font-mono truncate" data-testid="nda-pdf-hash">SHA-256 {doc.document_hash?.slice(0, 20)}…</span>
                : <span className="text-xs text-muted-foreground">Both parties finalize wording, then upload the PDF here to unlock signing.</span>}
      </div>

      {hasPdf && doc.status !== "fully_signed" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card-soft p-5 space-y-3" data-testid="dsc-section">
            <div className="flex items-center gap-2 font-medium text-sm"><Key size={16} /> DSC (USB token)</div>
            <p className="text-xs text-muted-foreground">PAdES signature. The hash is signed locally by your DSC bridge — your private key never leaves the token.</p>
            <select className="input-soft" value={party} onChange={(e) => setParty(e.target.value)} data-testid="sign-party-select">
              <option value="disclosing">Sign as disclosing · {doc.disclosing_party?.name || "Asymmetry Edits"}</option>
              <option value="receiving">Sign as receiving · {doc.receiving_party?.name || "Counterparty"}</option>
            </select>
            <button className="btn-primary btn-sm w-full" disabled={busy === "dsc"} onClick={signDsc} data-testid="dsc-sign-btn"><Key size={13} /> {busy === "dsc" ? "Signing…" : "Sign with DSC"}</button>
            {bridgeMissing && (
              <div className="text-xs rounded-md border border-[color:hsl(var(--warning))]/40 p-3 space-y-2" data-testid="bridge-missing">
                <div className="text-[color:hsl(var(--warning))]">Signer bridge not detected at {cfg?.bridge_url}. Install & run the DSC bridge utility with your token plugged in.</div>
                {cfg?.dsc_mode === "sandbox" && <button className="btn-ghost btn-sm" onClick={sandboxDsc} data-testid="dsc-sandbox-btn">Use sandbox test certificate instead</button>}
              </div>
            )}
          </div>

          <form className="card-soft p-5 space-y-3" onSubmit={requestEsign} data-testid="esign-section">
            <div className="flex items-center gap-2 font-medium text-sm"><Fingerprint size={16} /> Aadhaar eSign {cfg?.esign_mode === "simulated" && <span className="text-[10px] uppercase tracking-wider text-[color:hsl(var(--warning))]">simulated ESP</span>}</div>
            <p className="text-xs text-muted-foreground">Signer receives an OTP on their Aadhaar-linked mobile via the ESP{cfg?.esign_mode === "simulated" && ` (sandbox OTP ${cfg.sim_otp})`}.</p>
            <input className="input-underline" placeholder={`Signer name (default: ${partyName})`} value={signer.name} onChange={(e) => setSigner({ ...signer, name: e.target.value })} data-testid="esign-name-input" />
            <input className="input-underline" required placeholder="Mobile or email" value={signer.identifier} onChange={(e) => setSigner({ ...signer, identifier: e.target.value })} data-testid="esign-identifier-input" />
            <button type="submit" className="btn-primary btn-sm w-full" disabled={busy === "esign"} data-testid="esign-request-btn"><Fingerprint size={13} /> Send eSign request</button>
          </form>
        </div>
      )}

      {requests.length > 0 && (
        <div className="card-soft p-5" data-testid="esign-requests">
          <div className="label-eyebrow mb-2">eSign requests</div>
          {requests.map((r) => (
            <div key={r.txn} className="flex items-center justify-between text-sm py-2 border-b border-border/60 last:border-0" data-testid={`esign-req-${r.txn}`}>
              <div><span className="font-medium">{r.signer.name}</span> <span className="text-muted-foreground">· {r.signer.identifier} · {r.provider}</span></div>
              <div className="flex items-center gap-3">
                <span className={`text-xs uppercase tracking-wider ${r.status === "completed" ? "text-[color:hsl(var(--success))]" : "text-muted-foreground"}`} data-testid={`esign-status-${r.txn}`}>{r.status}</span>
                {r.status === "requested" && r.sign_url && <a className="btn-ghost btn-sm" href={r.sign_url} target="_blank" rel="noreferrer" data-testid={`esign-link-${r.txn}`}><ArrowSquareOut size={12} /> Open signer link</a>}
              </div>
            </div>
          ))}
        </div>
      )}

      {verify && (
        <div className="card-soft p-5" data-testid="verify-panel">
          <div className="flex items-center gap-2 mb-3">
            {verify.signature_count > 0 && verify.all_intact ? <ShieldCheck size={18} className="text-[color:hsl(var(--success))]" /> : <ShieldWarning size={18} className="text-[color:hsl(var(--warning))]" />}
            <div className="font-medium text-sm" data-testid="verify-summary">
              {verify.signature_count === 0 ? "No embedded signatures yet" : verify.all_intact ? `${verify.signature_count} signature${verify.signature_count > 1 ? "s" : ""} · document intact` : "Signature integrity problem"}
            </div>
          </div>
          <div className="text-xs font-mono text-muted-foreground break-all">Current SHA-256: {verify.sha256}</div>
          {!verify.trust_roots_configured && verify.signature_count > 0 && <div className="text-xs text-muted-foreground mt-1">Trust chain not checked — set DSC_TRUST_ROOTS_PEM to a CCA root bundle to verify signer identity.</div>}
          {verify.signatures.map((s) => (
            <div key={s.field} className="mt-4 border-t border-border/60 pt-3 text-sm grid md:grid-cols-2 gap-x-6 gap-y-1" data-testid={`verify-sig-${s.field}`}>
              <div className="md:col-span-2 font-medium">{s.field} · {s.name || "—"} <span className="text-xs text-muted-foreground">{s.reason}</span></div>
              <div><span className="text-muted-foreground">Signer:</span> {s.certificate.subject}</div>
              <div><span className="text-muted-foreground">Issuer:</span> {s.certificate.issuer}</div>
              <div><span className="text-muted-foreground">Signed at:</span> {fmtDate(s.signed_at)}</div>
              <div><span className="text-muted-foreground">Cert validity:</span> {fmtDate(s.certificate.not_before)} → {fmtDate(s.certificate.not_after)}</div>
              <div><span className="text-muted-foreground">Serial:</span> <span className="font-mono text-xs">{s.certificate.serial_number}</span></div>
              <div><span className="text-muted-foreground">Integrity:</span> {s.intact && s.valid ? "Intact · valid" : "FAILED"} · {s.coverage} {s.trusted && "· trusted chain"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
